import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MatchingDetailPanel } from './MatchingDetailPanel';
import { FormulairePositionnement } from '../immersion/FormulairePositionnement';

import {
  BoutonPrincipal,
  Carte,
  Chargement,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { useReferentiels } from '../../lib/useDocuments';
import { useAuth } from '../../auth/useAuth';
import { LienOrganisation } from '../repertoire/LienOrganisation';
import {
  usePositionner,
  useRefuserProposition,
  useSuggestions,
  type FiltresSuggestions,
  type LigneSuggestion,
  type SaisiePositionnement,
} from '../../lib/useMatching';
import { ErreurApi } from '../../lib/api';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

const LIBELLES_MOBILITE: Record<string, string> = {
  PERMIS_B: 'Permis B',
  TRANSPORTS_EN_COMMUN: 'Transports en commun',
  AUCUNE: 'Sans mobilité',
};

const FILTRES_VIDES: FiltresSuggestions = {};

function libelleMobilite(mobilite: string | null | undefined): string {
  if (mobilite === undefined) return '';
  if (mobilite === null) return ' · mobilité non déclarée';
  return ` · ${LIBELLES_MOBILITE[mobilite] ?? mobilite}`;
}

/**
 * Vue « suggestions » du chargé de relation entreprise et du recrutement.
 *
 * Les propositions viennent du moteur de matching, déjà calculées et classées
 * par score : l'écran ne recalcule rien, il donne à arbitrer. Le détail des
 * critères reste dépliable — un score qu'on ne peut pas contredire ne sert à
 * personne.
 */
export function SuggestionsMatching({ site }: { site: string }): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresSuggestions>(FILTRES_VIDES);
  const { data, isLoading, error } = useSuggestions(site, filtres);
  const vuePlacement = useAuth().utilisateur?.vueCandidat === 'PLACEMENT';
  const referentiels = useReferentiels();
  const refuser = useRefuserProposition();
  const positionner = usePositionner();
  const toast = useToast();

  const [aRefuser, setARefuser] = useState<LigneSuggestion | null>(null);
  const [motif, setMotif] = useState('');

  const modifier = (champ: keyof FiltresSuggestions, valeur: string): void => {
    setFiltres((precedents) => ({ ...precedents, [champ]: valeur || undefined }));
  };

  const actifs = Object.values(filtres).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <Carte titre="Filtres" sansBordure>
        <div className="space-y-3 p-4 md:p-6">
          {/* Une colonne au téléphone, deux sur tablette, cinq au bureau : les
              filtres restent lisibles sans jamais déborder en largeur. */}
          <div
            className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${vuePlacement ? '' : 'xl:grid-cols-5'}`}
          >
            {!vuePlacement && (
              <>
                <Champ libelle="Sexe">
                  <select
                    value={filtres.sexe ?? ''}
                    onChange={(evenement) => modifier('sexe', evenement.target.value)}
                    className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy"
                  >
                    <option value="">Tous</option>
                    <option value="F">Femmes</option>
                    <option value="M">Hommes</option>
                  </select>
                </Champ>

                <Champ libelle="Âge minimum">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={15}
                    max={99}
                    value={filtres.ageMin ?? ''}
                    onChange={(evenement) => modifier('ageMin', evenement.target.value)}
                    placeholder="15"
                    className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy"
                  />
                </Champ>

                <Champ libelle="Âge maximum">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={15}
                    max={99}
                    value={filtres.ageMax ?? ''}
                    onChange={(evenement) => modifier('ageMax', evenement.target.value)}
                    placeholder="99"
                    className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy"
                  />
                </Champ>
              </>
            )}

            <Champ libelle="Filière">
              <select
                value={filtres.formationCode ?? ''}
                onChange={(evenement) => modifier('formationCode', evenement.target.value)}
                className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy"
              >
                <option value="">Toutes</option>
                {(referentiels.data?.formations ?? []).map((formation) => (
                  <option key={formation.code} value={formation.code}>
                    {formation.titre}
                  </option>
                ))}
              </select>
            </Champ>

            <Champ libelle="Score minimum">
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={100}
                value={filtres.scoreMinimum ?? ''}
                onChange={(evenement) => modifier('scoreMinimum', evenement.target.value)}
                placeholder={String(data?.bareme.seuilProposition ?? 60)}
                className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-codialis-navy"
              />
            </Champ>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-codialis-gray">
            <span>
              Le filtre d'antenne reste celui de la barre de sites. Les propositions sous{' '}
              {data?.bareme.seuilProposition ?? 60} / 100 ne sont pas conservées par le moteur.
            </span>
            {actifs > 0 && (
              <button
                type="button"
                onClick={() => setFiltres(FILTRES_VIDES)}
                className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
              >
                Effacer les {actifs} filtre(s)
              </button>
            )}
          </div>
        </div>
      </Carte>

      {data && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Compteur valeur={data.compteurs.proposees} libelle="proposition(s) ouverte(s)" />
          <Compteur
            valeur={data.compteurs.fortScore}
            libelle={`à ${data.bareme.seuilNotification} / 100 ou plus`}
          />
          <Compteur valeur={data.compteurs.candidatsConcernes} libelle="candidat(s) concerné(s)" />
        </div>
      )}

      {(refuser.error instanceof ErreurApi || positionner.error instanceof ErreurApi) && (
        <MessageErreur
          message={
            (refuser.error instanceof ErreurApi && refuser.error.message) ||
            (positionner.error instanceof ErreurApi && positionner.error.message) ||
            'Action refusée.'
          }
        />
      )}

      {error && (
        <MessageErreur
          message={
            error instanceof ErreurApi
              ? error.message
              : 'Les suggestions n’ont pas pu être chargées.'
          }
        />
      )}
      {isLoading && <Chargement libelle="Chargement des suggestions…" />}

      {!isLoading && !error && (
        <div className="space-y-4">
          {(data?.suggestions ?? []).map((suggestion) => (
            <CarteSuggestion
              key={suggestion.id}
              suggestion={suggestion}
              libelles={data?.libelles ?? {}}
              seuilNotification={data?.bareme.seuilNotification ?? 85}
              enCours={positionner.isPending || refuser.isPending}
              onPositionner={(saisie) =>
                positionner.mutate(
                  {
                    ...saisie,
                    apprenantId: suggestion.candidat.id,
                    entrepriseId: suggestion.entreprise.id,
                    besoinId: suggestion.besoin.id,
                  },
                  { onSuccess: ({ message }) => toast(message) },
                )
              }
              onRefuser={() => {
                setMotif('');
                setARefuser(suggestion);
              }}
            />
          ))}

          {(data?.suggestions ?? []).length === 0 && (
            <Carte titre="Aucune suggestion" sansBordure>
              <p className="p-4 sm:p-6 text-sm text-codialis-gray">
                Aucune proposition ouverte sur ce périmètre. Relancer le calcul après avoir
                enregistré de nouveaux besoins entreprise ou de nouveaux candidats.
              </p>
            </Carte>
          )}
        </div>
      )}

      <Modale
        titre="Écarter cette proposition"
        sousTitre={
          aRefuser
            ? `${aRefuser.candidat.nom} · ${aRefuser.entreprise.denomination} · ${aRefuser.score} / 100`
            : undefined
        }
        ouverte={aRefuser !== null}
        onFermer={() => setARefuser(null)}
        largeur="max-w-lg"
      >
        <div className="space-y-4 p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            Le motif nourrit l'analyse des refus : c'est lui qui permet d'ajuster les poids des
            critères. Une proposition écartée n'est jamais reproposée automatiquement.
          </p>
          <textarea
            rows={3}
            aria-label="Motif de l’écart"
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder="Poste déjà pourvu, profil trop éloigné, entreprise injoignable…"
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setARefuser(null)}
              className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              Annuler
            </button>
            <BoutonPrincipal
              onClick={() => {
                if (!aRefuser) return;
                refuser.mutate(
                  { id: aRefuser.id, motif: motif.trim() },
                  {
                    onSuccess: () => {
                      toast('Proposition écartée, motif enregistré');
                      setARefuser(null);
                    },
                  },
                );
              }}
              disabled={motif.trim().length < 3 || refuser.isPending}
            >
              Écarter
            </BoutonPrincipal>
          </div>
        </div>
      </Modale>
    </div>
  );
}

function Champ({ libelle, children }: { libelle: string; children: JSX.Element }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
    </label>
  );
}

function Compteur({ valeur, libelle }: { valeur: number; libelle: string }): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-card">
      <div className="font-display text-2xl font-extrabold text-codialis-navy">{valeur}</div>
      <div className="text-xs text-codialis-gray">{libelle}</div>
    </div>
  );
}

function CarteSuggestion({
  suggestion,
  libelles,
  seuilNotification,
  enCours,
  onPositionner,
  onRefuser,
}: {
  suggestion: LigneSuggestion;
  /** Libellés des critères, rendus par l'API — jamais recopiés dans le front. */
  libelles: Record<string, string>;
  seuilNotification: number;
  enCours: boolean;
  onPositionner: (saisie: SaisiePositionnement) => void;
  onRefuser: () => void;
}): JSX.Element {
  const [deplie, setDeplie] = useState(false);
  const fortScore = suggestion.score >= seuilNotification;
  // Une donnée déduite ne se lit pas dans le score : elle doit se voir sans
  // avoir à déplier le détail, sinon le chiffre passe pour du déclaré.
  const donneeDeduite = (suggestion.details?.avertissements ?? []).includes(
    'DISPONIBILITE_DEDUITE_DE_LA_DATE_ENTREE',
  );

  return (
    <article className="rounded-2xl bg-white p-4 shadow-card md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/adm-dossier/${suggestion.candidat.id}`}
              className="font-display text-base font-bold text-codialis-navy hover:text-codialis-sky"
            >
              {suggestion.candidat.nom}
            </Link>
            <PastilleSite site={suggestion.candidat.siteCode} />
            {fortScore && <Pastille texte="Fort score" ton="success" />}
            {suggestion.candidat.rqth && <Pastille texte="RQTH" ton="info" />}
            {donneeDeduite && <Pastille texte="Dispo. déduite" ton="warning" />}
          </div>
          <div className="mt-1 text-sm text-codialis-navy">
            → <LienOrganisation id={suggestion.entreprise.id} nom={suggestion.entreprise.denomination} classeLien="hover:text-codialis-sky hover:underline transition" />
            {suggestion.entreprise.ville ? ` · ${suggestion.entreprise.ville}` : ''}
          </div>
          <div className="mt-1 text-[11px] text-codialis-gray">
            {suggestion.candidat.formation ?? 'Formation non arrêtée'}
            {suggestion.candidat.age != null ? ` · ${suggestion.candidat.age} ans` : ''}
            {suggestion.candidat.sexe ? ` · ${suggestion.candidat.sexe}` : ''}
            {libelleMobilite(suggestion.candidat.mobilite)}
          </div>
          <div className="mt-1 text-[11px] text-codialis-gray">
            {suggestion.details ? suggestion.details.placesRestantes : suggestion.besoin.postes} place(s) restante(s)
            {suggestion.besoin.dateSouhaitee
              ? ` · démarrage souhaité le ${jour(suggestion.besoin.dateSouhaitee)}`
              : ''}
          </div>
        </div>

        <div className="text-right">
          <div className="font-display text-3xl font-extrabold text-codialis-navy">
            {suggestion.score}
            <span className="text-base text-codialis-gray"> / 100</span>
          </div>
          <div className="progress-track mt-1 h-1.5 w-24 overflow-hidden rounded-full">
            <div className="progress-bar h-full" style={{ width: `${suggestion.score}%` }} />
          </div>
          <div className="mt-1 text-[10px] text-codialis-gray">
            proposé le {jour(suggestion.creeLe)}
          </div>
        </div>
      </div>

      {suggestion.besoin.profilRecherche && (
        <p className="mt-3 rounded-xl bg-codialis-sand p-3 text-xs text-codialis-navy">
          Profil recherché : {suggestion.besoin.profilRecherche}
        </p>
      )}

      <div className="mt-4">
        <FormulairePositionnement enCours={enCours} onValider={onPositionner}>
          <button
            type="button"
            onClick={onRefuser}
            disabled={enCours}
            className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-gray transition hover:bg-gray-50 disabled:opacity-50"
          >
            Écarter
          </button>
          <button
            type="button"
            onClick={() => setDeplie((valeur) => !valeur)}
            className="min-h-[44px] px-2 text-sm font-semibold text-codialis-sky hover:underline"
          >
            {deplie ? 'Masquer le détail' : 'Détail du score'}
          </button>
        </FormulairePositionnement>
      </div>

      {deplie && (
        suggestion.details ? (
          <MatchingDetailPanel adequation={suggestion.details} libelles={libelles} />
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-1 border-t border-gray-100 pt-3 sm:grid-cols-2">
            {suggestion.criteres.map((critere) => (
              <div
                key={critere.critere}
                className="flex items-baseline justify-between gap-3 text-[11px]"
              >
                <span className="text-codialis-gray">
                  {libelles[critere.critere] ?? critere.critere} · {critere.motif}
                </span>
                <span className="whitespace-nowrap font-mono text-codialis-navy">
                  {critere.points}/{critere.maximum}
                </span>
              </div>
            ))}
          </div>
        )
      )}
    </article>
  );
}
